import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import {
  DateFilterTypesEnum,
  EntityType,
  PaymentDueAccountVM,
  PaymentMethodVM,
  PaymentService
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import { UserLoginData } from "../../components/common/charts/Enums";
import FixedFooter from "../listings/add-listing-footer/fixed-footer";
import './payment.css';
import PayableCard from "../listings/listing-cards/payables";
import PaymentPayablesORReceivablesList from "./PaymentPayablesORReceivablesList";
import PaymentSummery from "./PaymentSummery";
import { URLS } from "../../constants/constants";
import DateHelper from "../../utils/DateHelper";
import Card from "../card";
import SimpleSpinner from "../../components/loader";
import { useCancelNavigation } from "../../utils/useCancelNavigation";

interface ApiData {
  paymentPayeeList: PaymentDueAccountVM[];
  paymentDetail: PaymentDueAccountVM | null;
  paymentMethod: PaymentMethodVM[];
}

interface PaymentSelectionState {
  selectedPayments: PaymentDueAccountVM[];
  totalAmount: number;
}

interface PaymentFormData {
  PaymentModeId: string;
  checkNum: string;
  transactionId: string;
  date: string;
}

const INITIAL_API_DATA: ApiData = {
  paymentMethod: [],
  paymentPayeeList: [],
  paymentDetail: null
};

const DATE_FILTERS = {
  NEXT_30_DAYS: DateFilterTypesEnum.Next30Days,
  NEXT_3_MONTHS: DateFilterTypesEnum.Next3Months,
  NEXT_6_MONTHS: DateFilterTypesEnum.Next6Months,
};

const PayablesPostPayment = () => {
  const [data, setData] = useState<ApiData>(INITIAL_API_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentState, setPaymentState] = useState<PaymentSelectionState>({
    selectedPayments: [],
    totalAmount: 0,
  });
  const [fullyQualifiedFilter, setFullyQualifiedFilter] = useState("paidFlowTypeId == 1");
  const [dateFilterType, setDateFilterType] = useState<number | null>(null);

  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));

  // Same entity resolution as EditPayable — read from URL search params
  const isEntity = searchParams.get('isEntity') === 'true';
  const entityId = Number(searchParams.get('entityId')) || undefined;
  const entityTypeId = Number(searchParams.get('entityTypeId')) || undefined;
  const payeeAccountId = searchParams.get('pai');

  const fetchInitialData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Entity-aware header API — same conditional logic as EditPayable
      let payablePromise;
      if (isEntity && entityTypeId === EntityType.Property && payeeAccountId && entityId) {
        payablePromise = apiHelper(
          PaymentService.getApiInvoicePayeeAccountPropertyHeader,
          { showNotification: false },
          Number(payeeAccountId),
          Number(entityId)
        );
      } else if (isEntity && entityTypeId === EntityType.Unit && payeeAccountId && entityId) {
        payablePromise = apiHelper(
          PaymentService.getApiInvoicePayeeAccountUnitHeader,
          { showNotification: false },
          Number(payeeAccountId),
          Number(entityId)
        );
      } else if (isEntity && entityTypeId === EntityType.Tenant && entityId) {
        payablePromise = apiHelper(
          PaymentService.getApiInvoicePayeeAccountTenantHeader,
          { showNotification: false },
          Number(entityId)
        );
      } else {
        // No entity — original fallback using route :id param
        payablePromise = apiHelper(
          PaymentService.getApiPaymentDueAccountPayable,
          { showNotification: false },
          id
        );
      }

      const [paymentMethod, paymentDetail] = await Promise.all([
        apiHelper(
          PaymentService.getApiPaymentMethodOrganizationlist,
          { showNotification: false },
          organizationId
        ),
        payablePromise,
      ]);

      setData(prev => ({
        ...prev,
        paymentMethod: paymentMethod?.data?.success || [],
        paymentDetail: paymentDetail?.data?.success || null
      }));
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load required data",
        type: NotificationType.Failure,
      });
    } finally {
      setIsLoading(false);
    }
  }, [id, organizationId, isEntity, entityTypeId, entityId, payeeAccountId]);


  const fetchPaymentPayeeList = useCallback(async () => {
    try {
      let res;

      if (isEntity && entityTypeId === EntityType.Property && payeeAccountId && entityId) {
        res = await apiHelper(
          PaymentService.getApiInvoicePayeeAccountPropertyList,
          { showNotification: false },
          Number(payeeAccountId),
          Number(entityId)
        );
      } else if (isEntity && entityTypeId === EntityType.Unit && payeeAccountId && entityId) {
        res = await apiHelper(
          PaymentService.getApiInvoicePayeeAccountUnitList,
          { showNotification: false },
          Number(payeeAccountId),
          Number(entityId)
        );
      } else if (isEntity && entityTypeId === EntityType.Tenant && entityId) {
        res = await apiHelper(
          PaymentService.getApiInvoicePayeeAccountTenantList,
          { showNotification: false },
          Number(entityId)
        );
      } else {
        const isDateFiltered = [DATE_FILTERS.NEXT_30_DAYS, DATE_FILTERS.NEXT_3_MONTHS, DATE_FILTERS.NEXT_6_MONTHS].includes(dateFilterType as number);

        res = await apiHelper(
          PaymentService.getApiPaymentDueAccountPayeelist,
          { showNotification: false },
          id,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          fullyQualifiedFilter,
          undefined,
          isDateFiltered ? dateFilterType : undefined,
          isDateFiltered ? DateHelper.getClientDate() : undefined,
          undefined,
          undefined,
          isDateFiltered ? "DueDate" : undefined
        );
      }

      setData(prev => ({
        ...prev,
        paymentPayeeList: res?.data?.success || []
      }));

      return res;
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load payment payee list",
        type: NotificationType.Failure,
      });
      throw error;
    }
  }, [isEntity, entityTypeId, payeeAccountId, entityId, dateFilterType, fullyQualifiedFilter, id]);


  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    fetchPaymentPayeeList();
  }, [fetchPaymentPayeeList]);

  const handleSetSelectedPayments = useCallback((payments: PaymentDueAccountVM[], totalAmount: number) => {

    setPaymentState({
      selectedPayments: payments,
      totalAmount,
    });
  }, []);

  const methods = useForm<PaymentFormData>({
    defaultValues: {
      PaymentModeId: "",
      checkNum: "",
      transactionId: "",
      date: new Date().toISOString().split('T')[0]
    }
  });

  const onNavigate = useCancelNavigation(
    "/payment?paymentMode=Payables&paidFlowTypeId=1"
  );

  const onSubmit = async (formData: PaymentFormData) => {



    if (paymentState.selectedPayments.length === 0) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Please select at least one payment",
        type: NotificationType.Warning,
      });
      return;
    }



    const transactionId = formData?.transactionId || crypto.randomUUID().replaceAll('-', '').toUpperCase();

    try {
      const paymentPromises = paymentState.selectedPayments.map(item => {

        const payload = {
          payDueAccountId: Number(item?.id),
          amount: Number(item.amount),
          fromPaymentMethodId: Number(formData?.PaymentModeId),
          checkNumber: formData?.checkNum,
          paymentDate: new Date(formData.date).toISOString(),
          transactionId: transactionId,
        };

        const paymentMethod = data.paymentMethod.find(x => x.id === Number(formData?.PaymentModeId))?.accountName;
        if (paymentMethod !== "Check" && paymentMethod !== "Cash") {
          delete payload.checkNumber;
        }

        return apiHelper(
          PaymentService.putApiPaymentDueAccountPay,
          {
            successMessage: "Payment settled successfully",
            failureMessage: "Unable to settle payment",
            showNotification: true,
          },
          payload
        );
      });

      await Promise.all(paymentPromises);
      methods.reset();
      onNavigate();
    } catch (error: any) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: error?.message,
        type: NotificationType.Warning,
      });
    }
  };

  if (isLoading) {
    return (
      <SimpleSpinner myClassName={"Icon50"} isFullPage={true} />
    );
  }

  return (
    <div className="payment-container d-flex flex-column gap-3 full-page-content">
      <PayableCard
        id="PayableDetailCard"
        className="payable-detail-header"
        isFooter={true}
        dataHookState={{
          rows: data.paymentDetail ? [data.paymentDetail] : [],
          loading: isLoading
        }}
      />
      {
        data.paymentDetail && (
          <>
            <Card className="no-bg p-0">
              <div className="row align-items-center mx-3 my-3">
                <div className="col-md-6">
                  <PaymentPayablesORReceivablesList
                    paymentMode={"Payables"}
                    payments={data.paymentPayeeList}
                    selectedPayments={paymentState.selectedPayments}
                    setSelectedPayments={handleSetSelectedPayments}
                    setDateFilterType={setDateFilterType}
                    setFullyQualifiedFilter={setFullyQualifiedFilter}
                    DATE_FILTERS={DATE_FILTERS}
                  />
                </div>
                <div className="col-md-6">
                  <div className="d-flex flex-column gap-3">
                    <FormProvider {...methods}>
                      <PaymentSummery
                        totalAmount={String(paymentState.totalAmount)}
                        paymentMethods={data.paymentMethod}
                        Payments={paymentState.selectedPayments}
                      />
                    </FormProvider>
                  </div>
                </div>
              </div>
            </Card>

            <FixedFooter
              onSubmit={methods.handleSubmit(onSubmit)}
              loading={isLoading}
              buttonLabel="Post Payments"
              onCancel={onNavigate}
            />
          </>
        )
      }

    </div>
  );
};

export default PayablesPostPayment;